import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Box, Dialog, Divider, FormHelperText, Stack, Typography, useTheme } from '@mui/material';

import {
  getFilterTalentsQueryKey,
  LocationUpdateDTO,
  ProcessResponseDTO,
  ProcessResponseDTOAssessmentType,
  ProcessUpdateDtoAssessmentType,
  TalentCreateDTO,
  TalentCreateDTOType,
  TalentListRequestDTO,
  TalentValidationResponseReason,
  useCreateTalent,
  useGetOrganizationStructure,
  useValidateTalent
} from '@/api/generated';
import ColoredPersonPlusIcon from '@/assets/icons/colored-person-plus.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import CommonDialogContent from '@/components/Dialog/CommonDialogContent';
import CommonDialogTitle from '@/components/Dialog/CommonDialogTitle';
import {
  buildMapStructure,
  buildRecursiveStructure,
  getFolderSlugForLocation
} from '@/components/fields/Location/locationTreeUtils';
import { CommonFormFooter } from '@/components/Form/Form';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useSnackbar } from '@/hooks/useSnackbar';
import useTalentBasicInfoForm from '@/hooks/useTalentBasicInfoForm';
import { TalentBasicInfoFields } from '@/views/TalentEditInfoModal/BasicInfo';
import { TalentBasicInfoViewType } from '@/views/TalentEditInfoModal/constants';

interface ProcessCreateTalentModalProps {
  onClose: () => void;
  process: ProcessResponseDTO;
  onTalentCreated?: (talentId: string) => void;
  queryBody?: TalentListRequestDTO;
}

const ProcessCreateTalentModal: React.FC<ProcessCreateTalentModalProps> = ({
  onClose,
  process,
  onTalentCreated,
  queryBody
}) => {
  const { t } = useTranslation(['validation', 'settings', 'talents']);
  const { spacing } = useTheme();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();

  const [validationError, setValidationError] = useState<string | null>(null);
  const { mutateAsync: validateTalent, isLoading: isValidating } = useValidateTalent();

  const { watch, setValue } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();

  const talentsToAdd = watch(getPrefixedFieldName('talentsToAdd'));

  const handleTalentCreated = useCallback(
    (talentId: string) => {
      if (talentsToAdd) {
        setValue(getPrefixedFieldName('talentsToAdd'), [...talentsToAdd, talentId], {
          shouldDirty: true,
          shouldTouch: true
        });
      }
    },
    [getPrefixedFieldName, setValue, talentsToAdd]
  );

  const { data: organizationStructure } = useGetOrganizationStructure(process.organization?.slug as string, {
    query: {
      enabled: !!process.organization?.slug
    }
  });

  const recursiveStructure = useMemo(
    () => organizationStructure && buildRecursiveStructure(organizationStructure),
    [organizationStructure]
  );

  const mapStructure = useMemo(
    () => (recursiveStructure ? buildMapStructure(recursiveStructure) : {}),
    [recursiveStructure]
  );

  const processLocation = useMemo(
    () =>
      getFolderSlugForLocation(process?.location, mapStructure) ||
      process?.location?.slug ||
      process?.organization?.slug,
    [process, mapStructure]
  );

  const talentType = useMemo(() => {
    switch (process.assessmentType) {
      case ProcessResponseDTOAssessmentType.EXTERNAL:
        return TalentCreateDTOType.CANDIDATE;
      case ProcessResponseDTOAssessmentType.INTERNAL:
        return TalentCreateDTOType.EMPLOYEE;
      case ProcessResponseDTOAssessmentType.ANY:
      default:
        return TalentCreateDTOType.CANDIDATE;
    }
  }, [process.assessmentType]);

  const { formMethods } = useTalentBasicInfoForm({
    defaultValues: {
      type: talentType,
      organizationSlug: process.organization?.slug,
      locations: processLocation ? [processLocation] : []
    }
  });

  const { mutateAsync: createTalent, isLoading } = useCreateTalent();

  const handleCreateTalent = useCallback(
    async (formData: TalentCreateDTO) => {
      try {
        setValidationError(null);

        if (!process.organization?.slug) {
          throw new Error('Organization slug is required');
        }

        const locations: LocationUpdateDTO[] = ((formData?.locations || []) as unknown as string[])
          .filter((locationSlug: string) => !!mapStructure[locationSlug])
          .map(
            (locationSlug: string) =>
              ({
                slug: locationSlug,
                type: mapStructure[locationSlug]!.type
              }) satisfies LocationUpdateDTO
          );

        const payload = {
          ...formData,
          locations: locations!
        };

        // TODO: Use only createTalent once backend unifies validation logic
        const validationResult = await validateTalent({ data: payload });

        if (!validationResult.canCreate) {
          let message = '';

          switch (validationResult.reason) {
            case TalentValidationResponseReason.ALREADY_EXISTS:
              message = t('validation:talent.alreadyExists');
              break;
            case TalentValidationResponseReason.HIDDEN_TALENT:
              message = t('validation:talent.hidden');
              break;
            case TalentValidationResponseReason.REQUIRED_FIELD_MISSING:
              message = t('validation:talent.requiredMissing');
              break;
            default:
              message = t('validation:alerts.failed');
          }

          setValidationError(message);

          return;
        }

        const createdTalent = await createTalent({
          organizationSlug: process.organization?.slug,
          data: payload
        });

        if (queryBody) {
          queryClient.invalidateQueries(getFilterTalentsQueryKey(queryBody, { includeStats: !queryBody.page }));
        }

        if (createdTalent?.id && process.id) {
          showMessage(t('settings:processSettings.form.alerts.saveSuccess'));
          handleTalentCreated(createdTalent.id);
          onTalentCreated?.(createdTalent.id);
          onClose();
        }
      } catch (error) {
        showMessage(t('validation:alerts.failed'));
      }
    },
    [
      process.organization?.slug,
      process.id,
      createTalent,
      queryBody,
      mapStructure,
      queryClient,
      showMessage,
      t,
      handleTalentCreated,
      onTalentCreated,
      onClose,
      validateTalent
    ]
  );

  useEffect(() => {
    const subscription = formMethods.watch(() => {
      if (validationError) setValidationError(null);
    });

    return () => subscription.unsubscribe();
  }, [formMethods, validationError]);

  if (isLoading) {
    return <BackdropLoader />;
  }

  return (
    <FormProvider {...formMethods}>
      <Dialog open={true} onClose={onClose} fullWidth>
        <CommonDialogTitle onClose={onClose}>
          <ProcessCreateTalentHeader />
        </CommonDialogTitle>
        <CommonDialogContent>
          <Box component="form" onSubmit={formMethods.handleSubmit(handleCreateTalent)}>
            <Divider />
            <TalentBasicInfoFields
              viewType={TalentBasicInfoViewType.CREATE}
              sx={{ padding: spacing(4) }}
              disabledFields={{
                type: process.assessmentType !== ProcessUpdateDtoAssessmentType.ANY,
                locations: true
              }}
            />

            {validationError && (
              <Box sx={{ px: 4, pb: 2, mt: -2 }}>
                <FormHelperText error sx={{ mx: 0, mb: 1 }}>
                  {validationError}
                </FormHelperText>
              </Box>
            )}

            <CommonFormFooter
              showResetButton={false}
              submitText={t('common:create')}
              buttonsContainerSx={{ justifyContent: 'flex-end' }}
              disableSubmit={isValidating || !!validationError}
            />
          </Box>
        </CommonDialogContent>
      </Dialog>
    </FormProvider>
  );
};

const ProcessCreateTalentHeader = () => {
  const { t } = useTranslation();
  const { spacing, palette } = useTheme();

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <Box sx={{ padding: spacing(0.5), borderRadius: spacing(0.5), background: palette.green[100] }}>
        <ColoredPersonPlusIcon display="block" width={44} height={44} />
      </Box>
      <Typography component="h2" variant="h3">
        {t('settings:processSettings.form.talents.addTalent')}
      </Typography>
    </Stack>
  );
};

export default ProcessCreateTalentModal;
